/**
 * HTTP service for the projects feature (PX-W6.3, TASK-EDD04F2F) — portfolio
 * list/detail, roster mutations and archive, all through the ONE shared
 * transport.
 *
 * Deliberately NO project create/update method exists in this service
 * (INV-1): a project body is a privilege-escalation surface (`members[]`,
 * ACL arrays) and roster changes go ONLY through the member endpoints below.
 * The ticket sends no project PUT/POST at all.
 */

import { apiClient } from '@/services'
import type {
  AddMemberInput,
  ProjectMemberRow,
  ProjectSummary,
  UpdateMemberInput
} from '../types'

/** Base path for every project-scoped route this service calls. */
const base = (orgId: string): string => `/${orgId}/projects`

export const projectService = {
  /**
   * The ACL-intersected, caller-redacted portfolio (`GET /{orgId}/projects`).
   *
   * @returns A JSON ARRAY of redacted project rows (never wrapped in an envelope).
   */
  async list(orgId: string): Promise<ProjectSummary[]> {
    return apiClient.get<ProjectSummary[]>(base(orgId))
  },

  /**
   * One project row (`GET /{orgId}/projects/:id`) — carries the caller-scoped
   * `permissions` block. Rejects with the keyed 403/404 envelope.
   */
  async get(orgId: string, projectId: string): Promise<ProjectSummary> {
    return apiClient.get<ProjectSummary>(`${base(orgId)}/${projectId}`)
  },

  /**
   * Soft-archive (`POST …/:id/archive`). Refused 409 while a version is ACTIVE.
   * @returns The archived row.
   */
  async archive(orgId: string, projectId: string): Promise<ProjectSummary> {
    return apiClient.post<ProjectSummary>(`${base(orgId)}/${projectId}/archive`)
  },

  /** The roster (`GET …/:projectId/members`). */
  async listMembers(orgId: string, projectId: string): Promise<ProjectMemberRow[]> {
    return apiClient.get<ProjectMemberRow[]>(`${base(orgId)}/${projectId}/members`)
  },

  /**
   * Add a member (`POST …/members`, 201 → the created row).
   * `input.side` is never set by the UI — the server stamps it from the
   * user's durable client binding.
   */
  async addMember(
    orgId: string,
    projectId: string,
    input: AddMemberInput
  ): Promise<ProjectMemberRow> {
    return apiClient.post<ProjectMemberRow>(`${base(orgId)}/${projectId}/members`, input)
  },

  /** Sparse member patch (`PUT …/members/:userId`, 200 → the updated row). */
  async updateMember(
    orgId: string,
    projectId: string,
    userId: string,
    patch: UpdateMemberInput
  ): Promise<ProjectMemberRow> {
    return apiClient.put<ProjectMemberRow>(
      `${base(orgId)}/${projectId}/members/${userId}`,
      patch
    )
  },

  /** Remove a member (`DELETE …/members/:userId`, 204 → void). */
  async removeMember(orgId: string, projectId: string, userId: string): Promise<void> {
    return apiClient.delete<void>(`${base(orgId)}/${projectId}/members/${userId}`)
  }
}
